import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ShieldCheck, Warning, Factory } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

// Paramètres réglementaires : fabricant légal imprimé sur l'étiquette MDR (historisé, admin uniquement).
export default function MdrSettingsPanel() {
  const [data, setData] = useState(null);
  const [legalName, setLegalName] = useState('');
  const [address, setAddress] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/settings/manufacturer`);
      setData(r.data); setLegalName(r.data.legal_name || ''); setAddress((r.data.address_lines || []).join('\n'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de chargement'); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/settings/manufacturer`, { legal_name: legalName, address_lines: address.split('\n').map(s => s.trim()).filter(Boolean), reason: reason || null });
      setData(r.data); setReason('');
      toast.success('Fabricant légal enregistré — appliqué aux prochaines étiquettes');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="mdr-settings-panel">
      <div className="bg-white border border-zinc-200 rounded-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><Factory size={20} />Fabricant légal (étiquette produit MDR)</h2>
            <p className="text-xs text-zinc-500 mt-1">Raison sociale et adresse imprimées dans la zone « FABRICANT » des étiquettes 100×62 mm. Toute modification est historisée (ancienne / nouvelle valeur, utilisateur, date, raison).</p>
          </div>
          {data && (data.confirmed
            ? <span className="flex items-center gap-1 text-xs px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm" data-testid="manufacturer-confirmed">Confirmé le {fmt(data.updated_at)} · {data.updated_by_name}</span>
            : <span className="flex items-center gap-1 text-xs px-2 py-1 bg-amber-50 text-amber-900 border border-amber-300 rounded-sm" data-testid="manufacturer-unconfirmed"><Warning size={14} />À compléter / confirmer : valeur par défaut non vérifiée</span>)}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div><Label className="text-xs">Raison sociale *</Label><Input value={legalName} onChange={e => setLegalName(e.target.value)} className="rounded-sm mt-1" data-testid="manufacturer-legal-name" /></div>
            <div><Label className="text-xs">Adresse (une ligne par ligne d'étiquette) *</Label><Textarea value={address} onChange={e => setAddress(e.target.value)} rows={4} className="rounded-sm mt-1 font-mono text-sm" data-testid="manufacturer-address" /></div>
            <div><Label className="text-xs">Raison de la modification</Label><Input value={reason} onChange={e => setReason(e.target.value)} placeholder="ex. Mise à jour du siège social" className="rounded-sm mt-1" data-testid="manufacturer-reason" /></div>
            <Button onClick={save} disabled={saving || !legalName.trim() || address.split('\n').filter(s => s.trim()).length < 2} className="rounded-sm bg-zinc-900 text-white" data-testid="manufacturer-save">{saving ? 'Enregistrement…' : 'Enregistrer et confirmer'}</Button>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Aperçu zone FABRICANT</p>
            <div className="border border-zinc-300 rounded-sm p-3 bg-zinc-50 text-right text-xs" data-testid="manufacturer-preview">
              <p className="text-[9px] text-zinc-400">FABRICANT</p>
              <p className="font-bold">{legalName || '—'}</p>
              {address.split('\n').filter(Boolean).map((l, i) => <p key={i} className="text-zinc-700">{l}</p>)}
            </div>
            {data?.history?.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Historique</p>
                <ul className="text-xs space-y-1" data-testid="manufacturer-history">
                  {[...data.history].reverse().map((h, i) => <li key={i} className="border-l-2 border-zinc-200 pl-2"><span className="text-zinc-500">{fmt(h.at)} · {h.by_name}</span> — {h.old_value?.legal_name} / {(h.old_value?.address_lines || []).join(', ')} → <strong>{h.new_value?.legal_name}</strong> / {(h.new_value?.address_lines || []).join(', ')}{h.reason ? ` · ${h.reason}` : ''}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-5 text-sm text-zinc-700 space-y-2" data-testid="mdr-notice">
        <h3 className="font-heading font-semibold flex items-center gap-2"><ShieldCheck size={18} />Périmètre de la traçabilité logicielle</h3>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>Identifiant HAMAC unique par dispositif, lot de résine figé au démarrage, CQ-01 avec photo stockée, non-conformités, réclamations et refabrications liées, historique des modifications (ancienne / nouvelle valeur, utilisateur, horodatage, raison).</li>
          <li>Les historiques et photos ne sont ni modifiables ni supprimables par les actions ordinaires de l'application (aucun point d'API de suppression ; accès réservé au personnel HAMAC authentifié).</li>
          <li>Étiquette produit : sans marquage CE, sans UDI, sans mention commerciale — identifiant interne Code 128 uniquement.</li>
          <li className="text-amber-900">Ces fonctions outillent le système qualité ; elles ne constituent ni une certification ni une preuve suffisante de conformité au règlement (UE) 2017/745 : documentation technique, procédures et validations restent de la responsabilité du fabricant.</li>
        </ul>
      </div>
    </div>
  );
}
